import {
  collection,
  getDocs,
  limit as firestoreLimit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Fahrten eines einzelnen Fahrers (Fahrerdetails, Tab „Fahrten").
 *
 * Bisher filterte die Detailseite die 20 neuesten Fahrten ALLER Fahrer
 * clientseitig nach dem Fahrer — die Liste war dadurch fast immer leer.
 * Unternehmer-Konten dürfen `rides` nur mit dem eigenen
 * `visibleToOperatorIds`-Filter abfragen (sonst verweigern die Rules die
 * ganze Abfrage).
 */

export const ACTIVE_RIDE_STATUSES = ['accepted', 'arrived', 'in_progress'];

const baseConstraints = (driverId, operatorId) => [
  ...(operatorId ? [where('visibleToOperatorIds', 'array-contains', operatorId)] : []),
  where('driverId', '==', driverId)
];

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Betrag wie in der Fahrtenliste: bezahlt → Festpreis → tatsächlich → geschätzt.
const rideAmount = (ride) => {
  const capturedCents = Number(ride.paymentCapturedAmountCents);
  if (capturedCents > 0) return capturedCents / 100;
  if (ride.isFestpreis && Number(ride.festpreisEur) > 0) return Number(ride.festpreisEur);
  if (Number(ride.fare) > 0) return Number(ride.fare);
  if (Number(ride.estimatedEarningEur) > 0) return Number(ride.estimatedEarningEur);
  return null;
};

const mapRide = (rideDoc) => {
  const data = rideDoc.data();
  return {
    id: rideDoc.id,
    status: data.status || '',
    start: data.start || '',
    destination: data.destination || '',
    createdAt: toDate(data.timestamp)?.toISOString() || null,
    scheduledTime: toDate(data.scheduledTime)?.toISOString() || null,
    amount: rideAmount(data),
    isKrankenfahrt: data.isKrankenfahrt === true,
    // für getRideStatusLabel („Angetreten" bei Vorbestellungen)
    orderType: data.orderType ?? null,
    convertedToOpenRide: data.convertedToOpenRide === true
  };
};

/**
 * Lädt eine Seite Fahrten, neueste zuerst. `cursor` ist der letzte Snapshot
 * der vorherigen Seite (für „Weitere laden").
 */
export const fetchDriverRidesPage = async ({ driverId, operatorId = null, pageSize = 10, cursor = null }) => {
  const constraints = [
    ...baseConstraints(driverId, operatorId),
    orderBy('timestamp', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    firestoreLimit(pageSize)
  ];
  const snapshot = await getDocs(query(collection(db, 'rides'), ...constraints));
  return {
    rides: snapshot.docs.map(mapRide),
    cursor: snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : cursor,
    hasMore: snapshot.docs.length === pageSize
  };
};

/** Hat der Fahrer gerade eine laufende Fahrt? (sperrt „Besetzt/Verfügbar setzen") */
export const driverHasActiveRide = async ({ driverId, operatorId = null }) => {
  const snapshot = await getDocs(query(
    collection(db, 'rides'),
    ...baseConstraints(driverId, operatorId),
    where('status', 'in', ACTIVE_RIDE_STATUSES),
    firestoreLimit(1)
  ));
  return !snapshot.empty;
};
